import * as React from 'react';

import { noDefault } from 'lib/eventWrappers';
import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';

import { CoordinationCard } from 'data/models/CoordinationCard';

import {
    AddPortCallReviewTab as ReviewTab,
    BatchRequestViewModel,
    CoordinationCardViewModel,
    ReviewTabMilestone
} from './review';

import {
    BerthStayDurationText,
    MetroMapFrameworkView,
    MilestoneHeaderRow,
    MilestoneScheduleDetailsRow
} from 'portcall/metromapframework/view';
import { PCModal } from 'portcall/pcmodal/view';
import { InPortOpsSummaryText, PortCallScheduleHeaderView } from 'portcall/portcallschedule/view';

import { frequencyDescriptionText } from './itineraryview';

export const AddPortCallReviewTabView = SuperCritical(({ model: m } : { model : ReviewTab }) =>
    <div className="addportcallreviewtab">
        <PortCallScheduleHeaderView className="schedule-header" model={m.ScheduleHeader} />
        <ScheduleView model={m} />
        {/* TODO: Info about PJOs? */}
        {m.requiresAgentAffirmation() ? 
            <div className="agent-affirmations-selector-shell">
                <label className="agent-affirmations-label">
                    <input
                        type="checkbox"
                        className="pc-breaker-input"
                        checked={m.hasAccepted()}
                        onChange={e => m.hasAccepted(e.currentTarget.checked)}
                        tabIndex={400}
                    />
                    <span className="pc-breaker-light aa-selector" />
                    {' '}
                    I have read and agreed to the indicated statutes.
                </label>
                {'   '}
                <a
                    className=""
                    href=""
                    onClick={noDefault(() => m.ShowAgentAffirmation(true))}
                    tabIndex={300}
                >
                    See agreement
                </a>
            </div> : <div></div>
        }
        <RepetitionInfo model={m} />
        <AgentAffirmationsModal model={m} />
    </div>
);

const AgentAffirmationsModal = SuperCritical(({ model: m } : { model : ReviewTab }) => {
    return (
        <PCModal className="agent-affirmations-modal" shown={() => m.ShowAgentAffirmation()} onBackDropClick={() => m.ShowAgentAffirmation(false)}>
            <div className="modal-content pc-card">
                <div className="pc-card-header pc-card-title">Agreement From Agents</div>
                <div className="modal-body">
                    <div>
                        I affirm that the operations to be performed under this booking are compliant with Section 266-61, Hawai‘i Revised Statutes.
                    </div>
                    <br />
                    <div>
                        I also affirm that I have read and understand the following:
                    </div>
                    <br />
                    <div>
                        Use of State of Hawai‘i Department of Transportation (HDOT) facilities, property, equipment, and waterways (collectively referred hereinafter as FACILITIES) is done so at my own risk.  HDOT does not guarantee the current condition of the FACILITIES or suitability of the FACILITIES for the intended operation(s). 
                    </div>
                    <br />
                    <div>
                        I acknowledge and agree it is my responsibility to validate the current condition of the FACILITIES prior to each use of such FACILITIES to ensure all operations can be performed safely and efficiently.  Additionally, in accordance with Section 19-41-5, Hawaii Administrative Rules (HAR), I agree to be governed by and pay all charges specified in the HAR.
                    </div>
                    <br />
                    <div className="buttons">
                        <a href="" className="cancel-btn" onClick={noDefault(() => m.ShowAgentAffirmation(false))}>Dismiss</a>
                        <button
                            className="submit-btn pc-button pc-button-primary"
                            onClick={() => {
                                m.hasAccepted(true);
                                m.ShowAgentAffirmation(false);
                            }}
                        >
                            I Agree
                        </button>
                    </div>
                </div>
            </div>
        </PCModal>
    );
});

const ScheduleView = SuperCritical(({ model: m } : { model : ReviewTab }) =>
    <div className="schedule">
        <MetroMapFrameworkView
            model={m.Framework}
            ctrl={{ milestoneContent: m => <MilestoneContent milestone={m} /> }}
        />
    </div>
);

const MilestoneContent = SuperCritical(({ milestone: m } : { milestone : ReviewTabMilestone }) =>
    <div>
        <MilestoneHeaderRow milestone={m} />
        <MilestoneScheduleDetailsRow milestone={m} />
        <MilestoneCoordinationsRow milestone={m} />
    </div>
);

const MilestoneCoordinationsRow = SuperCritical(({ milestone: m } : { milestone : ReviewTabMilestone }) =>
    <div className="portcall-milestone-row coordinations-row">
        <div className="portcall-milestone-row-label">
            {m.Date.IsBerthArrivalDate() && BerthStayDurationText(m)}
        </div>
        <div className="portcall-milestone-row-body">
            {m.Date.IsBerthArrivalDate() && <BerthAssignmentView milestone={m} />}
            {m.CoordinationCards().map(ccard =>
                <CCardView ccard={ccard} key={CCardKey(ccard)} />
            )}
        </div>
    </div>
);

const BerthAssignmentView = SuperCritical(({ milestone: m } : { milestone : ReviewTabMilestone }) =>
    <div className="coordination-card">
        <div className="header">
            <span className="coordination-title">Berth Assignment</span>
        </div>
        <div className="pc-text-smalllabel description">
            {m.Date.ItineraryPoint()?.BerthReservation() &&
                InPortOpsSummaryText(m.Date.ItineraryPoint()!.BerthReservation()!)
            }
        </div>
    </div>
        
);

let nextCCardKey = 0;
const CCARD_KEYS = new WeakMap<CoordinationCard, number>();
function CCardKey(ccard : CoordinationCardViewModel) {
    let id = CCARD_KEYS.get(ccard.Model);
    if (id === undefined) {
        id = nextCCardKey++;
        CCARD_KEYS.set(ccard.Model, id);
    }
    return id;
}

const CCardView = SuperCritical(({ ccard } : { ccard : CoordinationCardViewModel }) =>
    <div className="coordination-card">
        <div className="header">
            <span className="coordination-title">{ccard.Model.Coordination()?.Title()}</span>
            <span className="coordination-title-separator">|</span>
            <span className="coordination-provider">{ccard.Model.Provider()?.Provider()?.Name()}</span>
        </div>
        <div className="pc-text-smalllabel description">{ccard.Model.Coordination()?.Description()}</div>
    </div>
);

const RepetitionInfo = SuperCritical(({ model: m } : { model : ReviewTab }) =>
    m.BatchRequest() ?
        <RepeatedPortCall batchReq={m.BatchRequest()!} /> :
        <div className="repetition-info is-not-repeated">
            This PortCall is not repeated.
        </div>
);

const BATCH_DATES_FORMAT = 'HH:mm on DD MMM YYYY';

const RepeatedPortCall = SuperCritical(({ batchReq: b } : { batchReq : BatchRequestViewModel }) => {
    const
        batch = b.Model.Batch(),
        preview = b.Preview();
    return (
        <div className="repetition-info is-repeated">
            <div className="header">Range</div>
            <div className="batch-info-row">
                <label className="batch-info-label">Frequency</label>
                <span className="batch-info-body">
                    {batch && frequencyDescriptionText(b.StartDate(), batch.RecurrencePeriod)}
                </span>
            </div>
            <div className="batch-info-row">
                <label className="batch-info-label">Batch Size</label>
                <span className="batch-info-body">
                    {preview && `${preview.BatchSize} ${preview.BatchSize === 1 ? 'PortCall' : 'PortCalls'}`}
                </span>
            </div>
            <div className="batch-info-row">
                <label className="batch-info-label">First PortCall Arrival</label>
                <span className="batch-info-body">
                    {preview?.FirstPortCallArrival && tzmoment(preview.FirstPortCallArrival).format(BATCH_DATES_FORMAT)}
                </span>
            </div>
            <div className="batch-info-row">
                <label className="batch-info-label">Last PortCall Arrival</label>
                <span className="batch-info-body">
                    {preview?.LastPortCallArrival && tzmoment(preview.LastPortCallArrival).format(BATCH_DATES_FORMAT)}
                </span>
            </div>
        </div>
    );
});



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/reviewview.tsx